import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import { MessageCircle, Heart, Lightbulb, Bug, HelpCircle, Star, Loader2, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "love",     label: "Love it",           icon: Heart,       tint: "bg-hearth-coral/12 text-hearth-coral border-hearth-coral/40" },
  { key: "idea",     label: "I have an idea",    icon: Lightbulb,   tint: "bg-hearth-amber/15 text-[#7A5320] border-hearth-amber/50" },
  { key: "bug",      label: "Something's off",   icon: Bug,         tint: "bg-hearth-clay/12 text-hearth-clayDark border-hearth-clay/40" },
  { key: "question", label: "A question",        icon: HelpCircle,  tint: "bg-hearth-sage/15 text-hearth-sageDark border-hearth-sage/40" },
  { key: "other",    label: "Something else",    icon: MessageCircle, tint: "bg-hearth-stone text-hearth-mute border-hearth-line" },
];

export default function Feedback() {
  const [category, setCategory] = useState("love");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [message, setMessage] = useState("");
  const [context, setContext] = useState("");
  const [saving, setSaving] = useState(false);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMine = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/feedback/mine");
      setMine(data || []);
    } catch (e) {
      // Silent for the user — the compose form still works even if history
      // fails to load. Log so dev builds surface auth/network issues.
      console.debug("Feedback loadMine failed:", e);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadMine(); }, [loadMine]);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 4) return toast.error("A few more words, please.");
    setSaving(true);
    try {
      await api.post("/feedback", {
        message: message.trim(),
        category,
        rating: rating || null,
        context: context.trim() || null,
      });
      setMessage("");
      setRating(0);
      setContext("");
      setCategory("love");
      toast.success("Thank you.", { description: "We read every note — really." });
      loadMine();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const displayRating = hoverRating || rating;

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="THE MAIL SLOT"
        title="Tell us what you think."
        subtitle="Love it, hate it, wish it did the thing — this is a warm little inbox that goes straight to us. No canned response, no ticket number."
        icon={MessageCircle}
      />

      <form onSubmit={submit} data-testid="feedback-form" className="warm-card p-6 md:p-8 space-y-5">
        {/* Category chips */}
        <div>
          <Label className="caption text-hearth-mute mb-2 block">What&apos;s this about?</Label>
          <div className="flex flex-wrap gap-2" data-testid="feedback-categories">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const active = c.key === category;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCategory(c.key)}
                  data-testid={`feedback-cat-${c.key}`}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition ${
                    active
                      ? `${c.tint} shadow-sm scale-[1.02]`
                      : "border-hearth-line text-hearth-mute hover:bg-hearth-stone hover:text-hearth-ink"
                  }`}
                >
                  <Icon size={13}/> {c.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Rating (optional) */}
        <div>
          <Label className="caption text-hearth-mute mb-2 block">
            How would you rate HearthHouse today? <span className="italic">optional</span>
          </Label>
          <div className="flex items-center gap-1" data-testid="feedback-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n === rating ? 0 : n)}
                onMouseEnter={() => setHoverRating(n)}
                onMouseLeave={() => setHoverRating(0)}
                data-testid={`feedback-star-${n}`}
                className="transition-transform hover:scale-110"
                title={`${n} out of 5`}
              >
                <Star
                  size={26}
                  className={
                    n <= displayRating
                      ? "fill-hearth-amber text-hearth-amber"
                      : "text-hearth-mute/40"
                  }
                />
              </button>
            ))}
            {rating > 0 && (
              <span className="ml-2 caption text-hearth-mute">
                {["","one","two","three","four","five"][rating]} out of five
              </span>
            )}
          </div>
        </div>

        {/* Message */}
        <div>
          <Label className="caption text-hearth-mute mb-2 block" htmlFor="feedback-message">
            The note
          </Label>
          <textarea
            id="feedback-message"
            data-testid="feedback-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            maxLength={2000}
            placeholder="What&apos;s on your mind? A wish, a bug, a moment that surprised you…"
            className="input-warm w-full rounded-2xl p-4 resize-y min-h-[120px] leading-relaxed"
          />
          <div className="flex justify-between mt-1">
            <p className="text-xs italic text-hearth-mute">Short or long — whatever helps you say it.</p>
            <p className="text-xs text-hearth-mute">{message.length}/2000</p>
          </div>
        </div>

        {/* Context (optional) */}
        <div>
          <Label className="caption text-hearth-mute mb-2 block" htmlFor="feedback-context">
            Where were you? <span className="italic">optional</span>
          </Label>
          <Input
            id="feedback-context"
            data-testid="feedback-context"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="e.g. Spice Drawer, Grocery list, Family Table"
            className="input-warm h-11"
            maxLength={60}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
          <p className="text-xs text-hearth-mute flex items-center gap-1.5">
            <ShieldCheck size={12}/> Only the HearthHouse team sees this. Nothing is sold or shared —
            <Link to="/privacy" className="text-hearth-clay underline ml-1">privacy</Link>.
          </p>
          <Button
            type="submit"
            data-testid="feedback-submit"
            disabled={saving || message.trim().length < 4}
            className="rounded-full bg-hearth-ink hover:bg-black text-hearth-cream h-11 px-5"
          >
            {saving
              ? <Loader2 size={13} className="mr-1.5 animate-spin"/>
              : <Send size={13} className="mr-1.5"/>}
            Send it
          </Button>
        </div>
      </form>

      {/* Previously sent notes */}
      <section data-testid="feedback-history">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-display text-2xl text-hearth-ink">Your past notes</h3>
          <p className="caption text-hearth-mute">only visible to your household</p>
        </div>
        {loading && <div className="warm-card p-6 text-center caption text-hearth-mute"><Loader2 size={16} className="mx-auto animate-spin"/></div>}
        {!loading && mine.length === 0 && (
          <div className="warm-card p-8 text-center">
            <p className="text-hearth-mute italic">A quiet inbox. Send your first note above.</p>
          </div>
        )}
        {!loading && mine.length > 0 && (
          <div className="space-y-3">
            {mine.map((n) => {
              const cat = CATEGORIES.find((c) => c.key === n.category) || CATEGORIES[4];
              const Icon = cat.icon;
              return (
                <article key={n.id} className="warm-card p-5" data-testid={`feedback-item-${n.id}`}>
                  <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 chip ${cat.tint}`}>
                        <Icon size={12}/> {cat.label}
                      </span>
                      {n.rating && (
                        <span className="inline-flex items-center gap-0.5 text-hearth-amber">
                          {Array.from({ length: n.rating }).map((_, i) => (
                            <Star key={i} size={12} className="fill-hearth-amber"/>
                          ))}
                        </span>
                      )}
                      {n.context && <span className="caption text-hearth-mute">· from {n.context}</span>}
                    </div>
                    <p className="caption text-hearth-mute shrink-0">
                      {new Date(n.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                    </p>
                  </div>
                  <p className="text-hearth-ink leading-relaxed whitespace-pre-wrap">{n.message}</p>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
